/** @jsxImportSource @emotion/react */
import React, { forwardRef, useEffect, useState } from 'react';

import {
  BOYS_AND_MEN,
  BOYS_OR_MEN,
  BOYS_OR_MEN_MIXED,
  CLUB,
  FEMALE,
  GENDER_MIXED,
  GIRLS_AND_WOMEN_MIXED,
  GIRLS_OR_WOMEN,
  GIRLS_OR_WOMEN_MIXED,
  MALE,
  ONBOARDING_PREFERENCES,
} from '../../const';
import useLocalStorage from '../../hooks/useLocalStorage';
import ExpandableList from '../ExpandableList';
import Text from '../Text';
import useStyles from './styles';

export const teamClubRef = React.createRef();

const VenueClubList = forwardRef(
  (
    {
      title,
      titleAnalyticsId,
      data,
      type,
      isFiltered,
      toggleFiltered,
      cardAction,
      isClubInfo = false,
      clubname,
    },
    ref
  ) => {
    const styles = useStyles();
    const { ref1, ref2 } = ref;

    const { getLocalItem } = useLocalStorage();
    const preferencesObj =
      JSON.parse(getLocalItem(ONBOARDING_PREFERENCES)) || {};
    const { PlayWith } = preferencesObj;
    const [selectedGenders, setSelectedGenders] = useState([]);

    useEffect(() => {
      let defaultSelectedGenders = [];
      if (isFiltered) {
        switch (PlayWith) {
          case BOYS_OR_MEN_MIXED:
            defaultSelectedGenders = [MALE, GENDER_MIXED];
            break;
          case GIRLS_OR_WOMEN_MIXED:
            defaultSelectedGenders = [FEMALE, GENDER_MIXED];
            break;
          case GIRLS_AND_WOMEN_MIXED:
            defaultSelectedGenders = [MALE, FEMALE, GENDER_MIXED];
            break;
          case BOYS_OR_MEN:
            defaultSelectedGenders = [MALE];
            break;
          case GIRLS_OR_WOMEN:
            defaultSelectedGenders = [FEMALE];
            break;
          case BOYS_AND_MEN:
            defaultSelectedGenders = [MALE, FEMALE];
            break;
          default:
            defaultSelectedGenders = [];
            break;
        }
      }
      setSelectedGenders(defaultSelectedGenders);
    }, [isFiltered, PlayWith]);

    const handleGenderClick = gender => {
      setSelectedGenders(prevSelectedGenders =>
        prevSelectedGenders.includes(gender)
          ? prevSelectedGenders.filter(g => g !== gender)
          : [...prevSelectedGenders, gender]
      );
    };

    const filterData = genders => {
      if (genders.length === 0) return data;
      return data.map(category => ({
        ...category,
        data: category.data.filter(team => genders.includes(team.Gender)),
      }));
    };

    const handleKeyDown = (e, gender) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleGenderClick(gender);
      }
    };

    const filteredData = filterData(selectedGenders);
    return (
      <div ref={teamClubRef}>
        <div css={styles.base} tabIndex={-1} ref={ref1}>
          <div css={styles.header}>
            <Text
              id={titleAnalyticsId}
              as="h2"
              size="5"
              css={styles.title(isClubInfo)}
            >
              {title}
            </Text>
            <div css={styles.genderHeader}>
              <div
                css={styles.gender(selectedGenders.includes('Male'))}
                onClick={() => handleGenderClick('Male')}
                onKeyDown={e => handleKeyDown(e, 'Male')}
                className="Male"
                tabIndex={0}
                role="button"
                id="gender_male"
              >
                Male
              </div>
              <div
                css={styles.gender(selectedGenders.includes('Female'))}
                onClick={() => handleGenderClick('Female')}
                onKeyDown={e => handleKeyDown(e, 'Female')}
                className="Female"
                tabIndex={0}
                role="button"
                id="gender_female"
              >
                Female
              </div>
              <div
                css={styles.gender(selectedGenders.includes('Mixed'))}
                onClick={() => handleGenderClick('Mixed')}
                onKeyDown={e => handleKeyDown(e, 'Mixed')}
                className="Mixed"
                tabIndex={0}
                role="button"
                id="gender_mixed"
              >
                Mixed
              </div>
            </div>
            <Text css={styles.subtitle}>
              <input
                type="checkbox"
                value={title}
                css={styles.check}
                onChange={toggleFiltered}
                checked={isFiltered}
              />
              Only show teams matching my filters
            </Text>
          </div>
          <div css={styles.middle}>
            {filteredData &&
              filteredData.length > 0 &&
              filteredData.map((group, index) => {
                const defaultTemplate = (
                  <ExpandableList
                    index={index}
                    title={group.title}
                    ariaLabel={`${type === CLUB ? 'Teams' : 'Sessions'} on ${
                      group.title
                    }`}
                    list={group.data}
                    type={type}
                    cardAction={cardAction}
                    clubname={clubname}
                  />
                );
                return (
                  <div ref={ref2} key={group.title} css={styles.animateSession}>
                    {group.data && group.data.length > 0 && defaultTemplate}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    );
  }
);

export default VenueClubList;
